/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from 'react';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { Calendar } from '../../../../../shared/components/widgets/calendar/calendar.component';
import Button, {
  ButtonSize
} from '../../../../../shared/components/core-ui/button/button.component';
import {
  CustomPriceModel,
  UnitTypeUiModel
} from '../../../../../shared/ui-models/unit-type.ui-model';
import Select, { ValueType, components } from 'react-select';
import { Tooltip, withStyles } from '@material-ui/core';
import {
  InputText,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { ReservationType } from '../../../../../shared/enums/reservation-type';
import { FormElementData } from '../../../../../shared/interfaces/form-element-data.interface';

export interface ChooseApptProps {
  data: {
    disbaleDate: Date[];
    basePrice?: number;
    customPrices?: CustomPriceModel[];
    selectedUnitType: UnitTypeUiModel;
    reservationType: ReservationType;
  };
  eventHandlers: {
    setDatesAndDiscountFees: (
      unitType: Date | Date[],
      discountAmount?: number,
      discountFees?: string[]
    ) => void;
    proceedApptNextStep: (next: boolean) => void;
    cancelHandler: () => void;
  };
  blockDataComponent?: React.ReactNode;
}

const CustomTooltip = withStyles({
  tooltip: {
    fontSize: '1.4rem',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    borderRadius: '8px',
    padding: '8px 12px'
  }
})(Tooltip);

/**
 * Functional component ChooseAppt
 * Displays calendar for appointment selection with optional discount application
 */
export const ChooseAppt: React.FC<ChooseApptProps> = ({
  data,
  eventHandlers,
  blockDataComponent
}) => {
  const isMobileView = useWindowResizer();
  const [selectedDates, setSelectedDates] = useState<Date | Date[]>();
  const [fees, setFees] = useState<{ value: string; label: string }[] | null>(null);
  const [discount, setDiscount] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });

  const MultiValue = (props: any) => {
    const maxToShow = props.selectProps.maxToShow;
    const length = props.getValue().length;
    const shouldBadgeShow = length > maxToShow;
    const displayLength = length - maxToShow;

    const tooltipTitle = props
      .getValue()
      .map((item: any) => item.label)
      .join(', ');

    if (props.index < maxToShow) {
      return <components.MultiValue {...props} />;
    } else if (props.index === maxToShow) {
      return (
        <CustomTooltip title={tooltipTitle}>
          <div className="px-2 py-1 text-sm font-medium text-gray-600 bg-gray-50 rounded-md border border-gray-200">
            {shouldBadgeShow && `+ ${displayLength} item${length !== 1 ? 's' : ''}`}
          </div>
        </CustomTooltip>
      );
    }
    return null;
  };

  const isDiscountSection = data.reservationType === ReservationType.NORMAL_RESERVATION;
  const isBlockDatesSection = data.reservationType === ReservationType.BLOCK_DATES;

  return (
    <div className="w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Calendar Section */}
        <div className="mb-10">
          <div className="border-b border-gray-200 pb-4 mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">Calendar</h2>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-8 hover:border-gray-300 transition-colors duration-200">
            <Calendar
              data={{
                calenderView: true,
                disbaleDate: data.disbaleDate,
                showDateInput: false,
                basePriceTag: data.basePrice,
                customPriceTags: data.customPrices
              }}
              eventHandlers={{
                onChange: (val) => {
                  eventHandlers.setDatesAndDiscountFees(val);
                  setSelectedDates(val);
                }
              }}
            />
          </div>
        </div>

        {isDiscountSection && (
          <div className="mb-10">
            <div className="bg-white rounded-xl border border-gray-200 p-8 hover:border-gray-300 transition-colors duration-200">
              <div className="grid grid-cols-1 gap-8 mb-8">
                {/* Discount Amount Input */}
                <div>
                  <div className="relative">
                    <InputText
                      data={{
                        value: discount.value,
                        placeholder: 'Enter Discount Amount'
                      }}
                      config={{
                        maxRows: 1,
                        autoFocus: true,
                        type: InputTextType.number
                      }}
                      eventHandlers={{
                        onChangeHandler: (value: string, isValid: boolean) => {
                          setDiscount({ value, isValid });
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Discount Fees Select */}
                <div>
                  <Select
                    value={fees}
                    options={[{ value: 'Unit Price', label: 'rent' }].concat(
                      (data.selectedUnitType?.fees || []).map((fee) => ({
                        value: fee.name,
                        label: fee.name
                      }))
                    )}
                    isMulti
                    isSearchable
                    placeholder="Select Fees to Discount..."
                    onChange={(value: ValueType<{ value: string; label: string }[], false>) => {
                      setFees(value ? value : null);
                    }}
                    maxToShow={5}
                    components={{ MultiValue }}
                    classNamePrefix="react-select"
                  />
                </div>
              </div>

              {/* Discount Actions */}
              <div className="flex justify-end flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
                <Button
                  disabled={
                    !Array.isArray(selectedDates) ||
                    !selectedDates.length ||
                    !fees?.length ||
                    !discount?.value
                  }
                  onClick={() => {
                    if (discount && fees && selectedDates) {
                      eventHandlers.setDatesAndDiscountFees(
                        selectedDates,
                        discount.value,
                        fees.map((fee) => fee.value)
                      );
                    }
                  }}
                  size={isMobileView ? ButtonSize.full : ButtonSize.medium}
                  className="flex-1 sm:flex-initial bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Apply Discount
                </Button>
                <Button
                  disabled={!Array.isArray(selectedDates) || !selectedDates.length}
                  onClick={() => {
                    if (selectedDates) {
                      setFees(null);
                      setDiscount({ value: '', isValid: false });
                      eventHandlers.setDatesAndDiscountFees(selectedDates);
                    }
                  }}
                  size={isMobileView ? ButtonSize.full : ButtonSize.medium}
                  className="flex-1 sm:flex-initial bg-gray-200 hover:bg-gray-300 text-gray-700 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                >
                  Clear Discount
                </Button>
              </div>
            </div>
          </div>
        )}

        {isBlockDatesSection && (
          <div className="bg-white rounded-xl border border-gray-200 p-8 hover:border-gray-300 transition-colors duration-200">
            {blockDataComponent}
          </div>
        )}
      </div>
    </div>
  );
};
